import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import '../assets/css/components/program.css';
import { EarningsFormContainer, EarningFormField, EarningsOptions, OldEarningsTable, OnChange } from '../components/EarningsComponents';
import { RedemptionsTable, NewRedemptionForm } from '../components/RedemptionsComponents';
import * as Yup from 'yup';
import { useSearchParams } from 'react-router-dom';
import { InitiateTutorial } from '../components/Onboarding';
import { useFormikContext } from 'formik';

const ConditionalField = ({ name, children }) => {
  const { values } = useFormikContext();

  if (values[name]) {
    return children;
  }

  return null;
};

export default function Program() {
  const { getJwtToken, handleSignOut } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tutorial, setTutorial] = useState(0);
  const [program, setProgram] = useState(false);
  const [integrated, setIntegrated] = useState(true);
  // Earnings
  const [option, setOption] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [earningsErrors, setEarningsErrors] = useState(false);
  // Redemptions
  const [redemption, setRedemption] = useState(false);
  const [paid, setPaid] = useState(true); // Determines state of table
  const [couponType, setCouponType] = useState(false); // Determines state of form
  const [formValues, setFormValues] = useState(false); // initial values of Redemption form
  const urlRegExp = /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+.*)$/;

  useEffect(() => { 
    const tutorial = searchParams.get('tutorial');
    if (tutorial === 'true') setTutorial(1);
    const fetchProgramData = async () => {
        try {
            const token = await getJwtToken();
            const res = await axios.get('https://api.amplifyloyalty.com/app/stores', {
                headers: {
                    Authorization: `Bearer ${token}`
                },
      params: {
        events: 'Reward Redeemed'
      }
            });

        const isIntegrated = res.headers['x-integrated'];
        if (isIntegrated == 'false') setIntegrated(false);
        setProgram(res.data);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
              handleSignOut();
            }
        }
    };

    fetchProgramData();
  },[]);
  
  const getEmailLists = async (v) => {
    try {
      const token = await getJwtToken();
      let res = await axios.get('https://api.amplifyloyalty.com/app/stores/integrations/klaviyo', {
          headers: {
              Authorization: `Bearer ${token}`
          }
      });
        return res;
    } catch (err) {
      throw new Error(err);
    }
  }
  
  return (
    <div className="main">
      {tutorial === 1 && 
        <InitiateTutorial 
          text="In this guide, you will learn how to update your Ways to Earn, 
            add new ones, and create new Ways to Redeem."
          header="Program Setup" 
          setTutorial={setTutorial}
        />
      }
      <OldEarningsTable 
        program={[program, setProgram]} 
        option={[option, setOption]} 
        setIsAdding={setIsAdding} 
        setIsEditing={setIsEditing} 
        tutorial={tutorial}
        setTutorial={setTutorial}
      />
      {isAdding && 
        <EarningsOptions 
          program={program} 
          setIsAdding={setIsAdding} 
          setOption={setOption} 
          tutorial={tutorial}
          setTutorial={setTutorial}
        />
      }
      {option && ['purchase', 'account_creation', 'referral'].includes(option.key) &&
        <EarningsFormContainer
          program={[program, setProgram]} 
          option={[option, setOption]} 
          setIsAdding={setIsAdding} 
          editing={[isEditing, setIsEditing]}
          errors={[earningsErrors, setEarningsErrors]}
          schema={{
            points: Yup.number('Must be a number.').integer('Must be a whole number.').required('Field is required.').moreThan(0, 'Must be greater than 0.')
          }}
          tutorial={tutorial}
          setTutorial={setTutorial}
        />
      }
      {option && ['review'].includes(option.key) &&
        <EarningsFormContainer 
          program={[program, setProgram]} 
          option={[option, setOption]} 
          setIsAdding={setIsAdding} 
          editing={[isEditing, setIsEditing]}
          errors={[earningsErrors, setEarningsErrors]}
          schema={{
            points: Yup.number('Must be a number.').integer('Must be a whole number.').required('Field is required.').moreThan(0, 'Must be greater than 0.'),
            days_delayed: Yup.mixed('Must be mixed.').when('restrict_reviews', {
              is: true,
              then: Yup.number('Must be a number.').integer('Must be a whole number.').min(1, 'Must be greater than 0.').required('Field is required.'),
              otherwise: Yup.mixed('Must be mixed.').notRequired()
            }),
            restrict_reviews: Yup.boolean('Must be a boolean.'),
            integration: Yup.string('Must be a string.').when('status', {
              is: true,
              then: (schema) => schema.required('Field is required.'),
              otherwise: (schema) => schema.notRequired()
            })
          }}
        >
          <EarningFormField 
            type="select" 
            name="integration"
            label="Integration"
            options={[{value: 'judgeMe', name: 'Judge.me'}]}
            tooltip={{
              message: 'Before choosing an integration, be sure to enable it in the Integrations tab.',
              width: "125px"
            }}
          />
          <EarningFormField 
            type="checkbox" 
            name="restrict_reviews"
            label="Restrict reviews"
            inputClass="earnings-form__input"
            tooltip={{
              message: "Restrict how often a customer can receive points for leaving a review.",
              width: "225px"
            }}
          />
          <ConditionalField name="restrict_reviews">
            <div className="extra-fields">
              <EarningFormField 
                type="number" 
                name="days_delayed"
                label="Once every"
                sub={true}
                identifier="days"
                inputClass="earnings-form__input--number"
                tooltip={{
                  message: "If 30 is entered, the customer may only receive points for their review once every 30 days.",
                  width: "220px"
                }}
              />
            </div>
          </ConditionalField>
        </EarningsFormContainer>
      }
      {option && ['birthday'].includes(option.key) &&
        <EarningsFormContainer 
          program={[program, setProgram]} 
          option={[option, setOption]} 
          setIsAdding={setIsAdding} 
          editing={[isEditing, setIsEditing]}
          errors={[earningsErrors, setEarningsErrors]}
          schema={{
            points: Yup.number('Must be a number.').integer('Must be a whole number.').required('Field is required.').moreThan(0, 'Must be greater than 0.'),
            days_delayed: Yup.number('Must be a valid URL.').integer('Must be a whole number.').required('Field is required.').moreThan(-1, 'Must be greater than 0.'),
          }}
        >
          <EarningFormField 
            type="number" 
            name="days_delayed"
            label="Days delayed"
            identifier="days"
            inputClass="earnings-form__input--number"
            tooltip={{
              message: "If 10 is entered, the customer's birthday must be at least 10 days later than when they entered it. If 0 is entered, the customer can receive their points that day.",
              width: "225px"
            }}
          />
        </EarningsFormContainer>
      }
      {option && ['facebook','instagram','twitter'].includes(option.key) &&
        <EarningsFormContainer 
          program={[program, setProgram]} 
          option={[option, setOption]} 
          setIsAdding={setIsAdding} 
          editing={[isEditing, setIsEditing]}
          errors={[earningsErrors, setEarningsErrors]}
          schema={{
            points: Yup.number('Must be a number.').integer('Must be a whole number.').required('Field is required.').moreThan(0, 'Must be greater than 0.'),
            link: Yup.string().matches(urlRegExp, 'Must be a valid URL.').required('Field is required.')
          }}
        >
          <EarningFormField 
            type="text" 
            name="link"
            label="Link to profile"
          />
        </EarningsFormContainer>
      }
      {option && ['email','texts'].includes(option.key) &&
        <EarningsFormContainer 
          program={[program, setProgram]} 
          option={[option, setOption]} 
          setIsAdding={setIsAdding} 
          editing={[isEditing, setIsEditing]}
          errors={[earningsErrors, setEarningsErrors]}
          schema={{
            points: Yup.number('Must be a number.').integer('Must be a whole number.').required('Field is required.').moreThan(0, 'Must be greater than 0.'),
            integration: Yup.string().required('Field is required.'),
            list_id: Yup.string().required('Field is required.')
          }}
          >
          <EarningFormField 
            type="select" 
            name="integration"
            label="Integration"
            options={[{value: 'klaviyo', name: 'Klaviyo'}]}
            tooltip={{
              message: 'Before choosing an integration, be sure to enable it in the Integrations tab.',
              width: "125px"
            }}
          />
          <EarningFormField 
            type="select" 
            name="list_id"
            label="List Subscription"
            options={option.lists}
            tooltip={{
              message: 'Rewards will be given for first time subscribers to this list only.',
              width: "220px"
            }}
          />
          <OnChange 
            program={program} 
            setEarningsErrors={setEarningsErrors} 
            getEmailLists={getEmailLists}
            setOption={setOption}
            option={option}
          />
        </EarningsFormContainer>
      }
      <RedemptionsTable 
        program={program}
        setProgram={setProgram} 
        setRedemption={setRedemption} 
        setIsEditing={setIsEditing} 
        paid={[paid, setPaid]} 
        couponType={[couponType, setCouponType]}
        setFormValues={setFormValues}
        tutorial={tutorial}
        setTutorial={setTutorial}
      />
      {redemption &&
        <NewRedemptionForm 
          program={program}
          setProgram={setProgram}
          coupon={redemption}
          setCreatingCoupon={setRedemption}
          notIntegrated={!integrated}
          tutorial={tutorial}
          setTutorial={setTutorial}
        />
      }
    </div>
  )
}